import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Answers, QuestionId } from '@timeblock/assessment-content/assessments/follow-through-plan';

/**
 * Quiz state for the Follow-Through Plan funnel. Persisted so the plan, pricing, sign-up
 * and checkout pages (separate URLs) read the same answers, and a reload resumes in place.
 */
interface FollowThroughState {
  runId: string;
  answers: Answers;
  name: string;
  /** Index into the quiz step list (questions and explaining screens). */
  stepIndex: number;
  completed: boolean;
  /** Set once the account exists; checkout is tied to it. */
  accountEmail: string | null;
  setAnswer: (questionId: QuestionId, value: string | string[]) => void;
  setStepIndex: (index: number) => void;
  complete: (name: string) => void;
  setAccountEmail: (email: string) => void;
  reset: () => void;
}

function newRunId(): string {
  return `ftp_${crypto.randomUUID()}`;
}

const initialState = () => ({
  runId: newRunId(),
  answers: {},
  name: '',
  stepIndex: 0,
  completed: false,
  accountEmail: null,
});

export const useFollowThroughStore = create<FollowThroughState>()(
  persist(
    (set) => ({
      ...initialState(),
      setAnswer: (questionId, value) =>
        set((state) => ({ answers: { ...state.answers, [questionId]: value } })),
      setStepIndex: (stepIndex) => set({ stepIndex }),
      complete: (name) => set({ name, completed: true }),
      setAccountEmail: (accountEmail) => set({ accountEmail }),
      reset: () => set(initialState()),
    }),
    {
      name: 'lh-follow-through-plan',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/** zustand's persist rehydrates after the first render; pages wait for it before redirecting. */
export function useFollowThroughHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (useFollowThroughStore.persist.hasHydrated()) setHydrated(true);
    return useFollowThroughStore.persist.onFinishHydration(() => setHydrated(true));
  }, []);
  return hydrated;
}
